import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Search, SlidersHorizontal, Clock, MapPin } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import CounselorCard from '../../components/CounselorCard';
import BookingDialog from '../../components/BookingDialog';
import AppointmentActionDialog from '../../components/AppointmentActionDialog';
import PractitionerProfileDialog from '../../components/PractitionerProfileDialog';
import IMAGES from '../../../../constant/images';
import { getCounselorList } from '../../api/CounselorList';
import { addRemoveFavourite } from '../../api/AddRemoveFavourite';

const MemberDashboard = () => {
  const dispatch = useDispatch();
  const [collapsed, setCollapsed] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('Pune');
  const [counselors, setCounselors] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [upcomingAppointment, setUpcomingAppointment] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [successDialog, setSuccessDialog] = useState({ open: false, booking: null });

  // Get counselor data from Redux store
  const { data: counselorData, loading: counselorLoading } = useSelector(
    (state) => state.counselorList
  );
  

  // Handle favorite toggle
  const handleFavoriteToggle = async (counselor) => {
    try {
      setCounselors((prevCounselors) =>
        prevCounselors.map((c) =>
          c.counsellorLongId === counselor.counsellorLongId
            ? { ...c, isFavorite: !c.isFavorite }
            : c
        )
      );


      let payload;
      if (counselor.isFavorite) {
        // Removing from favorites
        payload = {
          usersFavouriteId: counselor.favouriteId,
          counsellorId: counselor.counsellorLongId,
          favouriteType: "COUNSELLOR",
          favourite: false,
        };
      } else {
        // Adding to favorites
        payload = {
          counsellorId: counselor.counsellorLongId,
          favouriteType: "COUNSELLOR",
          favourite: true,
        };
      }

      const result = await dispatch(addRemoveFavourite(payload)).unwrap();

      // Show success toast
      if (result.status === 'success') {
        const action = counselor.isFavorite ? 'removed from' : 'added to';
        toast.success(`Practitioner ${action} favorites!`, {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Refresh counselor list to get updated favorite data
        dispatch(getCounselorList({ limit: 50, offset: 0 }));
      }
    } catch (error) {
      // Revert optimistic update on error
      setCounselors((prevCounselors) =>
        prevCounselors.map((c) =>
          c.counsellorLongId === counselor.counsellorLongId
            ? { ...c, isFavorite: !c.isFavorite }
            : c
        )
      );

      // Show error toast
      toast.error('Failed to update favorites. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.error('Error toggling favorite:', error);
    }
  };

  // Fetch appointments and find the next upcoming one
  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/appointment/search?offset=0&limit=100&orderBy=id&direction=ASC`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            upcomingAppointment: true,
          }),
        }
      );

      const data = await response.json();

      if (data?.payload?.data && Array.isArray(data.payload.data)) {
        const now = moment();

        // Filter out appointments where end time has already passed
        const validUpcoming = data.payload.data.filter((apt) => {
          if (!apt.date || !apt.endTime) return false;
          const endTime = moment(`${apt.date} ${apt.endTime}`, 'YYYY-MM-DD HH:mm:ss');
          return endTime.isAfter(now);
        });

        // Sort by start time to get the nearest one
        const sortedUpcoming = validUpcoming.sort((a, b) => {
          const timeA = moment(`${a.date} ${a.startTime}`, 'YYYY-MM-DD HH:mm:ss');
          const timeB = moment(`${b.date} ${b.startTime}`, 'YYYY-MM-DD HH:mm:ss');
          return timeA.diff(timeB);
        });

        // Set the first upcoming appointment
        if (sortedUpcoming.length > 0) {
          setUpcomingAppointment(sortedUpcoming[0]);
        } else {
          setUpcomingAppointment(null);
        }
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Fetch counselors on component mount
  useEffect(() => {
    dispatch(getCounselorList({ limit: 50, offset: 0 }));
  }, [dispatch]);

  // Process counselor data when it changes
  useEffect(() => {
    if (counselorData?.status === 'success' && counselorData?.message?.payload?.data) {
      const counselorsList = counselorData.message.payload.data;

      // Map API data to CounselorCard format
      const mappedCounselors = counselorsList.map((counselor) => ({
        pcUserId: counselor.pcUserId || null,
        counsellorLongId: counselor.counsellorLongId || counselor.id,
        id: counselor.id || null,
        favouriteId: counselor.favouriteId || null,
        imageURL: counselor.imageUrl || null,
        firstName: counselor.firstName,
        lastName: counselor.lastName,
        title: counselor.title || 'Dr.',
        experience: counselor.experience || null,
        rating: counselor.averageRating || null,
        languages: counselor.languages ? counselor.languages.split(',').map(l => l.trim()) : ['Hindi', 'English'],
        price: counselor.hourlyPrice || null,
        duration: '1 hour',
        degree: counselor.degree && Array.isArray(counselor.degree) && counselor.degree.length > 0 ? counselor.degree : null,
        specializations: counselor.specialization ? [counselor.specialization] : [],
        consultationCount: counselor.numberOfSession || 0,
        nextAvailableSlot: counselor.nextAvailableSlotDate && counselor.nextAvailableSlot?.startTime
          ? formatNextSlot(counselor.nextAvailableSlotDate, counselor.nextAvailableSlot.startTime)
          : null,
        isFavorite: counselor.favourite || false,
        // Additional fields for dialog
        modes: counselor.modes || null,
        address: counselor.address || null,
        city: counselor.city || null,
        state: counselor.state || null,
        about: counselor.about || null,
        latestRatings: counselor.latestRatings || [],
      }));

      setCounselors(mappedCounselors);

      // Extract unique specializations for categories
      const uniqueSpecializations = [
        'All',
        ...new Set(
          counselorsList
            .map((c) => c.specialization)
            .filter(Boolean)
        ),
      ].sort();
      setCategories(uniqueSpecializations);
    }
  }, [counselorData]);

  // Format next available slot
  const formatNextSlot = (date, time) => {
    if (!date || !time) return null;

    // Parse as a local date; new Date('YYYY-MM-DD') would treat it as UTC
    const slotDate = moment(date, 'YYYY-MM-DD');
    const timeFormatted = formatTime(time);

    if (slotDate.isSame(moment(), 'day')) {
      return `Today, ${timeFormatted}`;
    } else if (slotDate.isSame(moment().add(1, 'day'), 'day')) {
      return `Tomorrow, ${timeFormatted}`;
    } else {
      return `${slotDate.format('MMM D')}, ${timeFormatted}`;
    }
  };

  // Format time from 24h to 12h format
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Calculate time until appointment
  const getTimeUntilAppointment = (appointment) => {
    if (!appointment?.date || !appointment?.startTime) return null;
    const aptTime = moment(`${appointment.date} ${appointment.startTime}`, 'YYYY-MM-DD HH:mm:ss');
    const now = moment();
    const duration = moment.duration(aptTime.diff(now));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return 'Now';
    }
  };

  // Filter and sort counselors
  const filteredCounselors = useMemo(() => {
    // First filter
    let filtered = counselors.filter((counselor) => {
      const matchesCategory =
        selectedFilter === 'All' ||
        counselor.specializations.some((spec) =>
          spec.toLowerCase().includes(selectedFilter.toLowerCase())
        );

      const matchesSearch =
        searchQuery === '' ||
        `${counselor.firstName} ${counselor.lastName}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        counselor.specializations.some((spec) =>
          spec.toLowerCase().includes(searchQuery.toLowerCase())
        );

      return matchesCategory && matchesSearch;
    });

    // Then sort
    const sorted = [...filtered];
    switch (sortBy) {
      case 'rating-high':
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'rating-low':
        return sorted.sort((a, b) => (a.rating || 0) - (b.rating || 0));
      case 'experience-high':
        return sorted.sort((a, b) => (b.experience || 0) - (a.experience || 0));
      case 'experience-low':
        return sorted.sort((a, b) => (a.experience || 0) - (b.experience || 0));
      case 'price-high':
        return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      case 'price-low':
        return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      case 'available':
        return sorted.sort((a, b) => {
          // Prioritize counselors with next available slot
          if (a.nextAvailableSlot && !b.nextAvailableSlot) return -1;
          if (!a.nextAvailableSlot && b.nextAvailableSlot) return 1;
          return 0;
        });
      case 'name':
        return sorted.sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
      default:
        return sorted;
    }
  }, [counselors, selectedFilter, searchQuery, sortBy]);

  // Handle book now button click
  const handleBookNow = (counselor) => {
    setSelectedCounselor(counselor);
    setBookingDialogOpen(true);
  };

  // Handle booking dialog close
  const handleBookingDialogClose = () => {
    setBookingDialogOpen(false);
    setSelectedCounselor(null);
    // Refresh counselor list to get updated availability
    dispatch(getCounselorList({ limit: 50, offset: 0 }));
  };

  // Handle view profile button click
  const handleViewProfile = (counselor) => {
    setSelectedCounselor(counselor);
    setProfileDialogOpen(true);
  };

  // Handle profile dialog close
  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
    setSelectedCounselor(null);
  };

  return (
    <>
      {/* Success Dialog */}
      <AppointmentActionDialog
        open={successDialog.open}
        onClose={() => setSuccessDialog({ open: false, booking: null })}
        type="success"
        title="Successful...!"
        message="Your appointment has been successfully booked."
        actionText="View Booking"
        onAction={() => {
          setSuccessDialog({ open: false, booking: null });
          window.location.href = '/member/my-bookings';
        }}
      />

      {/* Booking Dialog - Outside main layout to be on top of everything */}
      <BookingDialog
        open={bookingDialogOpen}
        onClose={handleBookingDialogClose}
        counselor={selectedCounselor}
        onBookingSuccess={(booking, actionType) => {
          handleBookingDialogClose();
          fetchAppointments();
          // Show success dialog
          setSuccessDialog({ open: true, booking: booking || {} });
        }}
      />

      {/* Practitioner Profile Dialog */}
      <PractitionerProfileDialog
        open={profileDialogOpen}
        onClose={handleProfileDialogClose}
        counselor={selectedCounselor}
      />

      <div className="relative flex min-h-screen bg-[#F8FAFC]">
        <TopBar />

        {/* Sidebar - Only visible on desktop */}
        <div className={`hidden lg:flex ${collapsed ? "w-[64px]" : "w-[272px]"} fixed top-0 left-0 h-screen z-20`}>
          <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        </div>

      {/* Main Content */}
      <div
        className={`flex-1 min-w-0 bg-[#F8FAFC] pt-16 px-2 pb-8 transition-all duration-300 ml-0 ${
          collapsed ? "lg:ml-[64px]" : "lg:ml-[272px]"
        }`}
      >
        <div className="w-full mx-auto pt-2">
          <div className="p-4 md:p-6 bg-[#FFFFFF] min-h-screen rounded-2xl">
            {/* Appointment Banner - Only show if there's an upcoming appointment */}
            {upcomingAppointment && (
              <div className="bg-[#EFF6FF] border-[1px] border-solid border-[#BFDBFE] rounded-2xl p-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <p className="text-xs sm:text-sm font-semibold font-poppins text-[#171717] flex-1 mb-0">
                  Your upcoming appointment with {upcomingAppointment.counsellorTitle || 'Dr.'} {upcomingAppointment.counsellorFirstName} {upcomingAppointment.counsellorLastName}
                </p>
                <div className="bg-white rounded-xl px-3 py-1.5 flex items-center gap-1.5 flex-shrink-0 shadow-sm">
                  <Clock size={16} className="text-[#2563EB]" />
                  <span className="text-xs sm:text-sm font-semibold text-[#4B61FF] font-poppins">
                    {getTimeUntilAppointment(upcomingAppointment)}
                  </span>
                </div>
              </div>
            )}

          {/* Page Title */}
          <h1 className="text-xl font-bold text-[#171717] font-poppins mb-3 sm:mb-4">
            Find a Practitioner in simple steps
          </h1>

          {/* Search and Filter Section */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-3 sm:mb-4">
            {/* Location Dropdown */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm px-3 h-9 flex items-center gap-2 w-full sm:w-auto sm:min-w-[180px]">
              <MapPin size={16} className="text-[#2563EB] flex-shrink-0" />
              <span className="text-sm text-[#171717] font-poppins flex-1 leading-[1.2]">{location}</span>
              <ChevronDown size={16} className="text-[#64748B]" />
            </div>

            {/* Search Bar */}
            <div className="flex-1 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm px-3 h-9 flex items-center gap-2 w-full sm:min-w-[200px] focus-within:ring-2 focus-within:ring-[#B3D7FF]">
              <Search size={16} className="text-[#94A3B8] flex-shrink-0" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 outline-none border-0 text-sm text-[#171717] placeholder:text-[#94A3B8] font-poppins bg-transparent leading-[1.2]"
              />
            </div>

            {/* Submit Button */}
            <button className="btn-gradient rounded-xl h-9 px-[14px] text-[13px] font-semibold border-0 font-poppins w-full sm:w-auto">
              Submit
            </button>
          </div>

          {/* Filter Tabs and Sort */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
            <div className="flex gap-2 flex-wrap overflow-x-auto w-full sm:w-auto">
              {categories.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-[12px] h-[32px] rounded-full font-poppins text-xs sm:text-[13px] font-semibold transition-colors whitespace-nowrap ${
                    selectedFilter === filter
                      ? 'bg-[#2563EB] text-white border-0'
                      : 'bg-[#EFF6FF] text-[#334155] border-0 hover:bg-[#DBEAFE]'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-auto">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center justify-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold font-poppins border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition w-full sm:w-auto"
              >
                <span>Sort</span>
                <SlidersHorizontal size={16} />
              </button>

              {/* Sort Dropdown Menu */}
              {showSortMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setSortBy('default');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'default' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Default
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('available');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'available' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Available First
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('rating-high');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'rating-high' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Rating: High to Low
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('rating-low');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'rating-low' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Rating: Low to High
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('experience-high');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'experience-high' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Experience: High to Low
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('experience-low');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'experience-low' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Experience: Low to High
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('price-high');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'price-high' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Price: High to Low
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('price-low');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'price-low' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Price: Low to High
                    </button>
                    <button
                      onClick={() => {
                        setSortBy('name');
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[13px] font-poppins border-0 hover:bg-[#F8FAFC] ${
                        sortBy === 'name' ? 'bg-[#EFF6FF] text-[#2563EB] font-semibold' : 'text-[#334155]'
                      }`}
                    >
                      Name (A-Z)
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Count */}
          <p className="text-sm text-[#171717] mb-4 font-poppins font-semibold">
            {filteredCounselors.length} Result{filteredCounselors.length !== 1 ? 's' : ''} found
          </p>

            {/* Counselor Cards Grid */}
            {counselorLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563EB] mx-auto"></div>
                  <p className="mt-4 text-sm text-[#334155] font-poppins">Loading practitioners...</p>
                </div>
              </div>
            ) : filteredCounselors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 pb-4">
                {filteredCounselors.map((counselor, index) => (
                  <CounselorCard
                    key={index}
                    counselor={counselor}
                    onFavoriteToggle={handleFavoriteToggle}
                    onBookNow={handleBookNow}
                    onViewProfile={handleViewProfile}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-base text-[#334155] font-poppins">No practitioners found</p>
                  <p className="text-sm text-[#64748B] font-poppins mt-2">Try adjusting your search or filters</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default MemberDashboard;
